/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import CustomListView from 'src/components/customTable/CustomListView';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  makeStyles,
  Tabs,
  Tab,
} from '@material-ui/core';
import setTitle from 'src/utils/setTitle';
import { useNavigate } from 'react-router';
import CatalogueDialog from 'src/components/CatalogueDialog';
import { pimApis } from 'src/api/pim';
import { debounce } from 'lodash';
import toast from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import FileUpload from 'src/components/FileUpload';
import RestartAltIcon from '@material-ui/icons/RestartAlt';

const useStyles = makeStyles((theme) => ({
  activeButton: {
    background: 'rgba(7, 83, 0, 0.1)',
    color: 'rgba(29, 122, 21, 1)',
    width: '100px',
    '&:hover': {
      // Disable hover effect
      background: 'rgba(7, 83, 0, 0.1)',
      color: 'rgba(29, 122, 21, 1)',
    },
  },
  inActiveButton: {
    background: 'rgba(255, 161, 161, 0.3)',
    color: 'red',
    width: '100px',
    '&:hover': {
      // Disable hover effect
      background: 'rgba(255, 161, 161, 0.3)',
      color: 'red',
    },
  },
  tabs: {
    '& .MuiTab-root': {
      backgroundColor: 'transparent',
      borderRadius: '4px',
      minWidth: '120px',
      minHeight: '30px',
    },
    '& .MuiTabs-indicator': {
      bottom: '6px',
    },
    '& .Mui-selected': {
      backgroundColor: '#231F20',
      color: '#fff',
    },
  },
}));

const Catalogs = () => {
  const classes = useStyles();
  const saerchRef = useRef(null);
  const [addCatalogue, setAddCatalogue] = useState(false);
  const [editCatalogue, setEditCatalogue] = useState(false);
  const [currentCatalogue, setCurrentCatalogue] = useState(null);
  const [cataloguesData, setCataloguesData] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [count, setCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSort, setSelectedSort] = useState<any>({
    id: '',
    value: 'asc',
  });
  const [uploadFile, setUploadFile] = useState(false);

  const [activeTab, setActiveTab] = useState('all');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  setTitle('Catalogues | PIM | ERP');
  const navigate = useNavigate();

  const fetchCatalogs = useCallback(async () => {
    try {
      // Determine the isActive parameter based on the active tab
      let isActiveParam;
      if (activeTab === 'active') {
        isActiveParam = true;
      } else if (activeTab === 'inactive') {
        isActiveParam = false;
      } else {
        isActiveParam = undefined;
      }

      const res = await pimApis.getCatalogues(
        page,
        pageSize,
        searchTerm,
        selectedSort,
        isActiveParam
      );

      setCataloguesData(res?.data?.data);
      setCount(res?.data?.totalRecords);
    } catch (error) {
      console.error('Error fetching catalog data:', error);
    }
  }, [page, pageSize, searchTerm, selectedSort, activeTab]);

  const columns = [
    {
      id: 'name',
      label: 'Name',
      isSort: true,
      width: '25%',
    },
    {
      id: 'description',
      label: 'Description',
      isSort: true,
      width: '40%',
    },
    {
      id: 'numberOfCategories',
      label: 'No. Categories',
      isSort: true,
      width: '40%',
    },
    // numberOfCategories
    {
      id: 'isActive',
      label: 'Status',
      width: '15%',

      Cell: (rowStatus, row: any) => (
        <Button
          variant="contained"
          className={rowStatus ? classes.activeButton : classes.inActiveButton}
          onClick={async (e) => {
            e.stopPropagation();
            const { _id, isActive } = row; // Extract the id and status from the row
            try {
              await pimApis.updateCatalogue(_id, { isActive: !rowStatus }); // Pass the inverse of the current status
              toast.success(
                `Catalogue status updated to ${
                  !rowStatus ? 'Active' : 'In active'
                }`
              );
              fetchCatalogs(); // Refresh the list after updating
            } catch (error) {
              toast.error('Error updating status');
              console.error('Error updating catalogue status:', error);
            }
          }}>
          {rowStatus ? 'Active' : 'In Active'}
        </Button>
      ),
      isSort: true,
    },
    {
      id: 'action',
      label: 'Action',
      width: '10%',

      Cell: (rowId, row) => (
        <EditIcon
          fontSize="small"
          onClick={(e) => {
            setCurrentCatalogue(row);
            setEditCatalogue(true);
            e.stopPropagation();
          }}
        />
      ),
    },
  ];

  // Create a debounced version of the setSearchTerm function
  const debouncedSetSearchTerm = debounce((value) => {
    setSearchTerm(value);
  }, 700); // Adjust the delay as needed

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  const handlePage = (_page: any) => {
    setPage(_page);
  };

  const handlePageSize = (_pageSize: any) => {
    setPageSize(_pageSize);
  };

  const handleOpenCatalogue = () => {
    setAddCatalogue(true);
  };

  const handleCloseCatalogue = () => {
    setAddCatalogue(false);
    setEditCatalogue(false);
    setCurrentCatalogue(null);
  };

  const handleEditClick = (row) => {
    const { _id, name } = row; // Extract the id and name from the row
    localStorage.setItem('catalogueName', name); // Save catalogueName in local storage
    navigate(`${_id}`); // Pass the catalogue name in the state
  };

  const handleSubmit = async (values) => {
    try {
      if (currentCatalogue) {
        // If editing, update the catalogue
        await pimApis.updateCatalogue(currentCatalogue._id, values); // Assuming this is the correct method
        toast.success('Catalogue updated successfully');
      } else {
        // If adding, create a new catalogue
        await pimApis.addCatalogue(values); // Assuming this is the correct method
        toast.success('Catalogue added successfully');
      }
      fetchCatalogs(); // Refresh the list
      handleCloseCatalogue(); // Close the modal after successful submission
    } catch (error) {
      toast.error('Something went wrong');
      console.error('Error creating/updating catalogue:', error);
    }
  };

  // function to handle tab change
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page on tab change
  };

  const handleFileUpload = useCallback(
    async (file: any) => {
      const formData = new FormData();
      console.log('handleFileUpload-1', file);
      formData.append('file', selectedFile ?? file);

      try {
        await pimApis.uploadCatalogueFile(formData);
        toast.success('File uploaded successfully');
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchCatalogs();
      } catch (error) {
        toast.error(error.response?.data?.message || 'File type not supported');
      }
    },
    [fetchCatalogs]
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      handleFileUpload(file);
    }
  };
  const handleCloseUpload = () => {
    setUploadFile(false);
  };
  const handleOpenUpload = () => {
    setUploadFile(true);
  };
  const handleSubmitUpload = async (file) => {
    setSelectedFile(file[0]);
    handleFileUpload(file[0]);
  };

  const handleResetFilters = () => {
    if (saerchRef.current) {
      saerchRef.current.value = ''; // Clears the text in the input field
    }
    setSearchTerm(''); //
    // setFromDate(null); //
    // setToDate(null); //
    setActiveTab('all'); //
    setPage(0); //
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <Typography color="textPrimary" variant="h3">
          Catalogues
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            color="primary"
            // onClick={() => fileInputRef.current?.click()} // Trigger file input click
            onClick={handleOpenUpload}>
            Upload File
          </Button>

          <Button
            variant="contained"
            color="secondary"
            title="Add Catalogue"
            sx={{ marginLeft: 2 }}
            onClick={handleOpenCatalogue}>
            Add Catalogue
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleResetFilters}
            title="Reset to defaults"
            sx={{
              minWidth: '50px',
              ml: 1,
            }}>
            <RestartAltIcon />
          </Button>
        </Box>
      </Box>

      <Box
        mt={2}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        <TextField
          inputRef={saerchRef}
          variant="outlined"
          placeholder="Search..."
          onChange={(e) => debouncedSetSearchTerm(e.target.value)} // Use the debounced function
          InputProps={{ style: { height: '40px', width: '240px' } }}
        />
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          className={classes.tabs}>
          <Tab label="All" value="all" />
          <Tab label="Active" value="active" />
          <Tab label="Inactive" value="inactive" />
        </Tabs>
      </Box>

      {/* Render the CustomListView based on the active tab */}
      <CustomListView
        onRowClick={handleEditClick}
        columns={columns}
        data={cataloguesData || []}
        page={page}
        pageSize={pageSize}
        count={count}
        setPageSize={handlePageSize}
        setPage={handlePage}
        selectedSort={selectedSort}
        setSelectedSort={setSelectedSort}
      />

      <CatalogueDialog
        open={addCatalogue}
        onClose={handleCloseCatalogue}
        onSubmit={handleSubmit}
        initialValues={{ name: '', description: '' }}
        title="Add Catalogue"
      />
      <FileUpload
        open={uploadFile}
        onClose={handleCloseUpload}
        onSubmitt={handleSubmitUpload}
        // initialValues={cateInitialValues}
        title="Upload your catalogue"
      />

      {editCatalogue && (
        <CatalogueDialog
          open={editCatalogue}
          onClose={() => setEditCatalogue(false)}
          onSubmit={handleSubmit}
          initialValues={{
            name: currentCatalogue?.name,
            description: currentCatalogue?.description,
          }}
          title="Edit Catalogue"
        />
      )}
    </Container>
  );
};

export default Catalogs;
